import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { X, RefreshCw, Send, ExternalLink, Loader2, Ban, Receipt } from "lucide-react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { unitLabel } from "@/components/admin/ServiceVariantsEditor";

const STATUS = {
  paid: ["Payé", "bg-emerald-50 text-emerald-700"],
  active: ["Actif", "bg-emerald-50 text-emerald-700"],
  open: ["En attente de paiement", "bg-amber-50 text-amber-700"],
  incomplete: ["En attente de paiement", "bg-amber-50 text-amber-700"],
  past_due: ["En retard", "bg-rose-50 text-rose-700"],
  unpaid: ["Impayé", "bg-rose-50 text-rose-700"],
  canceled: ["Résilié", "bg-neutral-100 text-neutral-600"],
  void: ["Annulée", "bg-neutral-100 text-neutral-500"],
  uncollectible: ["Non recouvrable", "bg-rose-50 text-rose-700"],
  draft: ["Brouillon", "bg-neutral-100 text-neutral-600"],
};
const fmtDate = (iso) => (iso ? new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" }) : "—");
const money = (v, c) => new Intl.NumberFormat("fr-FR", { style: "currency", currency: c || "EUR" }).format(v || 0);

function BillForm({ service, onDone }) {
  const billable = (service.variants || []).filter((v) => v.active && v.price > 0 && v.price_unit !== "on_quote");
  const [variantId, setVariantId] = useState(billable[0]?.id || "");
  const [q, setQ] = useState("");
  const [companies, setCompanies] = useState([]);
  const [company, setCompany] = useState(null);
  const [email, setEmail] = useState("");
  const [days, setDays] = useState(30);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    const t = setTimeout(() => api.get(`/admin/companies?per_page=20&q=${encodeURIComponent(q)}`).then((r) => setCompanies(r.data.items || [])).catch(() => {}), 250);
    return () => clearTimeout(t);
  }, [q]);

  const pick = async (c) => {
    setCompany(c); setQ(c.name); setCompanies([]);
    try { const r = await api.get(`/admin/shared-services/billing-contact/${c.id}`); setEmail(r.data.email || ""); } catch { setEmail(""); }
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!company) { toast.error("Choisissez une entreprise membre"); return; }
    const v = billable.find((x) => x.id === variantId);
    const recurring = ["per_month", "per_year"].includes(v?.price_unit);
    if (!window.confirm(`${recurring ? "Créer un abonnement" : "Envoyer une facture"} Stripe à ${company.name} (${email || "e-mail par défaut"}) pour « ${v?.name} » — ${money(v?.price, service.currency)} ${unitLabel(v?.price_unit)} ?`)) return;
    setBusy(true);
    try {
      await api.post(`/admin/shared-services/${service.id}/bill`, { variant_id: variantId, company_id: company.id, email, days_until_due: days, note });
      toast.success(recurring ? "Abonnement créé — 1re facture envoyée par Stripe" : "Facture Stripe envoyée");
      setCompany(null); setQ(""); setEmail(""); setNote("");
      onDone();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur Stripe"); }
    finally { setBusy(false); }
  };

  if (!billable.length) return <p className="rounded-xl bg-amber-50 p-3 text-xs text-amber-800">Ajoutez une variante avec un prix (mensuel, annuel ou paiement unique) pour pouvoir facturer.</p>;
  return (
    <form onSubmit={submit} className="grid gap-2 rounded-xl border border-wine-100 bg-wine-50/40 p-3 sm:grid-cols-2" data-testid="bill-form">
      <div className="relative sm:col-span-2">
        <input value={q} onChange={(e) => { setQ(e.target.value); setCompany(null); }} placeholder="Rechercher l'entreprise membre…" className="w-full rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="bill-company-search" />
        {!company && q && companies.length > 0 && (
          <div className="absolute z-10 mt-1 max-h-52 w-full overflow-y-auto rounded-lg border border-neutral-200 bg-white shadow-lg">
            {companies.map((c) => <button type="button" key={c.id} onClick={() => pick(c)} className="block w-full px-3 py-1.5 text-left text-sm hover:bg-wine-50" data-testid={`bill-company-option-${c.id}`}>{c.name} <span className="text-[10px] text-neutral-400">{c.status}</span></button>)}
          </div>
        )}
      </div>
      <select value={variantId} onChange={(e) => setVariantId(e.target.value)} className="rounded-lg border border-neutral-200 px-2 py-2 text-sm" data-testid="bill-variant">
        {billable.map((v) => <option key={v.id} value={v.id}>{`${v.name} — ${money(v.price, service.currency)} ${unitLabel(v.price_unit)}${v.stripe_price_id ? "" : " (non synchronisée)"}`}</option>)}
      </select>
      <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="E-mail de facturation" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="bill-email" />
      <label className="flex items-center gap-2 text-xs text-neutral-600">Échéance <input type="number" min="0" max="120" value={days} onChange={(e) => setDays(parseInt(e.target.value) || 0)} className="w-16 rounded-lg border border-neutral-200 px-2 py-1.5 text-sm" data-testid="bill-days" /> jours</label>
      <input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Mention sur la facture (optionnel)" className="rounded-lg border border-neutral-200 px-3 py-2 text-sm" data-testid="bill-note" />
      <button type="submit" disabled={busy} className="inline-flex items-center justify-center gap-2 rounded-full bg-wine-500 px-4 py-2 text-sm font-bold text-white hover:bg-wine-600 disabled:opacity-60 sm:col-span-2" data-testid="bill-submit">
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />} Facturer via Stripe
      </button>
    </form>
  );
}

/** Admin: per-service billing history (invoices, subscriptions, payment-link purchases). */
export default function ServiceSubscriptionsModal({ service, onClose }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [syncError, setSyncError] = useState(null);

  const load = useCallback(async (refresh = false) => {
    setLoading(true);
    try {
      const r = await api.get(`/admin/shared-services/${service.id}/subscriptions${refresh ? "?refresh=true" : ""}`);
      setItems(r.data.items || []); setSyncError(r.data.sync_error);
    } catch { toast.error("Erreur de chargement"); }
    finally { setLoading(false); }
  }, [service.id]);

  useEffect(() => { load(true); }, [load]);

  const cancel = async (r) => {
    if (!window.confirm(`Résilier l'abonnement de ${r.company_name} à la fin de la période en cours ?`)) return;
    try { await api.post(`/admin/shared-services/${service.id}/subscriptions/${r.id}/cancel`); toast.success("Résiliation programmée"); load(true); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Erreur"); }
  };

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4" onClick={onClose} data-testid="service-subs-modal">
      <div className="max-h-[92vh] w-full max-w-4xl overflow-y-auto rounded-2xl bg-white p-6 shadow-2xl" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between gap-3">
          <h2 className="flex items-center gap-2 text-xl font-bold text-navy"><Receipt className="h-5 w-5 text-wine-500" />Souscriptions — {service.title}</h2>
          <div className="flex items-center gap-1">
            <button onClick={() => load(true)} className="inline-flex items-center gap-1 rounded-full border border-neutral-200 px-3 py-1 text-xs font-semibold hover:bg-neutral-50" data-testid="subs-refresh"><RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} />Actualiser depuis Stripe</button>
            <button onClick={onClose} className="rounded-full p-1 hover:bg-neutral-100" data-testid="subs-close"><X className="h-5 w-5" /></button>
          </div>
        </div>
        <div className="mt-4"><BillForm service={service} onDone={() => load(false)} /></div>
        {syncError && <p className="mt-3 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="subs-sync-error">Synchronisation Stripe impossible : {syncError}</p>}
        <div className="mt-4 overflow-x-auto">
          <table className="w-full min-w-[720px] text-sm" data-testid="subs-table">
            <thead className="text-left text-[10px] uppercase tracking-wider text-neutral-500">
              <tr><th className="pb-2">Date</th><th className="pb-2">Membre</th><th className="pb-2">Variante</th><th className="pb-2 text-right">Montant</th><th className="pb-2">Statut</th><th className="pb-2 text-right">Actions</th></tr>
            </thead>
            <tbody>
              {items.map((r) => {
                const st = r.cancel_at_period_end && r.status === "active" ? ["Résiliation programmée", "bg-neutral-100 text-neutral-700"] : STATUS[r.status] || [r.status || "—", "bg-neutral-100 text-neutral-600"];
                return (
                  <tr key={r.id} className="border-t border-neutral-100" data-testid={`subs-row-${r.id}`}>
                    <td className="py-2 text-xs text-neutral-600">{fmtDate(r.created_at)}</td>
                    <td className="py-2"><div className="font-semibold text-navy">{r.company_name || "—"}</div><div className="text-[11px] text-neutral-500">{r.email}{r.kind === "payment_link" ? " · via lien de paiement" : ""}</div></td>
                    <td className="py-2 text-xs">{r.variant_name} <span className="text-neutral-400">{unitLabel(r.price_unit)}</span></td>
                    <td className="py-2 text-right font-semibold">{money(r.amount, r.currency)}</td>
                    <td className="py-2"><span className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${st[1]}`}>{st[0]}</span>{r.kind === "subscription" && r.invoice_status && r.invoice_status !== "paid" && <div className="mt-0.5 text-[10px] text-amber-700">Dernière facture : {(STATUS[r.invoice_status] || [r.invoice_status])[0]}</div>}{r.sync_error && <div className="mt-0.5 text-[10px] text-rose-600">{r.sync_error}</div>}</td>
                    <td className="py-2 text-right">
                      <div className="inline-flex items-center gap-1">
                        {r.hosted_invoice_url && <a href={r.hosted_invoice_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 rounded-full bg-neutral-100 px-2 py-0.5 text-[11px] font-semibold hover:bg-neutral-200" data-testid={`subs-invoice-${r.id}`}><ExternalLink className="h-3 w-3" />Facture{r.invoice_number ? ` ${r.invoice_number}` : ""}</a>}
                        {r.stripe_subscription_id && r.status !== "canceled" && !r.cancel_at_period_end && <button onClick={() => cancel(r)} className="inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold text-rose-600 hover:bg-rose-50" data-testid={`subs-cancel-${r.id}`}><Ban className="h-3 w-3" />Résilier</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {!loading && items.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-sm text-neutral-500" data-testid="subs-empty">Aucune souscription pour ce service.</td></tr>}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
